import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { useMemo } from "react"
import { z } from "zod"

import {
  type EditorConfig,
  useEditorConfig,
} from "../../../../../../EditorConfig"
import {
  type MediaAsset,
  updateMediaCache,
} from "../../../../hooks/mediaLibraryData"
import { type Tag, updateTagCache } from "../../../../hooks/tagData"
import { getCommonMediaTags } from "./SidepaneMetadataTags.utils"
import { type addOrRemove, Tags } from "./Tags"

interface MultiMediaMetadataTagsProps {
  selectedMedia: MediaAsset[]
}

export function MultiMediaMetadataTags(props: MultiMediaMetadataTagsProps) {
  const { selectedMedia } = props
  const config = useEditorConfig()

  const commonMediaTags = useMemo(
    () => getCommonMediaTags(selectedMedia),
    [selectedMedia],
  )

  async function toggleAssetTag(tag: Tag, action: addOrRemove) {
    const isRemove = action === "remove"
    const assetIds = selectedMedia.map((media) => media.id)
    let mediasRollback: MediaAsset[] = []

    updateMediaCache((medias) => {
      mediasRollback = medias

      return medias.map((media) => {
        if (assetIds.includes(media.id)) {
          return isRemove
            ? removeTagFromMedia(media, tag)
            : addTagToMedia(media, tag)
        }
        return media
      })
    })

    try {
      await addOrRemoveTagFromAssets({
        tagId: tag.id,
        assetIds,
        operation: isRemove ? "remove" : "add",
        config,
      })

      // The changesCount will be negative if the tag was removed from Assets
      const changesCount = selectedMedia.length * (isRemove ? -1 : 1)
      updateTagCache((tags) =>
        tags.map((cachedTag) => {
          if (cachedTag.id === tag.id) {
            return { ...cachedTag, count: cachedTag.count + changesCount }
          }
          return cachedTag
        }),
      )
    } catch (e) {
      updateMediaCache(() => mediasRollback)

      throw e
    }
  }
  function getTagsLabel() {
    const numberOfTags = commonMediaTags.length
    if (numberOfTags > 1) {
      return `${numberOfTags} tags in common`
    }
    if (numberOfTags === 1) {
      return "1 tag in common"
    }
    return "Common tags"
  }

  return (
    <Tags
      tags={commonMediaTags}
      getTagsLabel={getTagsLabel}
      toggleAssetTag={toggleAssetTag}
    />
  )
}

interface AssignOrUnassignTagAssetsArgs {
  tagId: string
  assetIds: string[]
  operation: "add" | "remove"
  config: EditorConfig
}

function addOrRemoveTagFromAssets(params: AssignOrUnassignTagAssetsArgs) {
  const { tagId, assetIds, config, operation } = params

  if (!config.mediaLibrary) throw new Error("Media Library not configured.")

  const { assetsApiBaseUrl, repository, authStrategy } = config

  return request(new URL(`tags/${tagId}/assets`, assetsApiBaseUrl), {
    headers: { repository },
    body: {
      assetIds,
    },
    method: operation === "add" ? "POST" : "DELETE",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
  })
}

function addTagToMedia(media: MediaAsset, tag: Tag): MediaAsset {
  const doesMediaAlreadyIncludeTag = media.tags.some(({ id }) => id === tag.id)
  if (doesMediaAlreadyIncludeTag) return media

  return {
    ...media,
    tags: [...media.tags, tag],
  }
}

function removeTagFromMedia(media: MediaAsset, tag: Tag): MediaAsset {
  const updatedTags = media.tags.filter((mediaTag) => mediaTag.id !== tag.id)

  return {
    ...media,
    tags: updatedTags,
  }
}
